import { useEffect, useRef, type KeyboardEvent, type MouseEvent } from 'react';
import type { FormMeasure } from '@/music/score/types';

interface ScoreFormOverviewProps {
  measures: readonly FormMeasure[];
  activeIndex: number;
  selectedIds: ReadonlySet<string>;
  visibleIds: ReadonlySet<string>;
  playingIndex: number | null;
  onNavigate: (index: number, extend: boolean) => void;
  /** Bars wrap into rows (the phone's Go to bar popover) instead of one strip. */
  grid?: boolean;
}

function structureLabel(measure: FormMeasure): string {
  const labels: string[] = [];
  if (measure.section) labels.push(`section ${measure.section}`);
  if (measure.open === 'repeat') labels.push('repeat start');
  if (measure.ending != null) labels.push(`ending ${measure.ending || 'bracket'}`);
  if (measure.barRepeat) labels.push(`${measure.barRepeat}-bar repeat`);
  if (measure.segno) labels.push('segno');
  if (measure.coda) labels.push('coda');
  if (measure.directive) labels.push(measure.directive);
  if (measure.close === 'repeat') labels.push('repeat end');
  else if (measure.close === 'final') labels.push('final barline');
  else if (measure.close === 'double') labels.push('double barline');
  if (measure.timeSig) labels.push(`${measure.timeSig[0]}/${measure.timeSig[1]} time`);
  return labels.join(', ');
}

export function ScoreFormOverview({
  measures,
  activeIndex,
  selectedIds,
  visibleIds,
  playingIndex,
  onNavigate,
  grid = false,
}: ScoreFormOverviewProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const followedIndex = playingIndex ?? activeIndex;

  useEffect(() => {
    const track = trackRef.current;
    const active = track?.querySelector<HTMLElement>(`[data-form-index="${followedIndex}"]`);
    if (!track || !active) return;
    if (grid) {
      // Rows: the bar you are on opens in view, without animating the panel open.
      const top = active.offsetTop - track.offsetTop;
      const bottom = top + active.offsetHeight;
      if (top < track.scrollTop) track.scrollTop = top;
      else if (bottom > track.scrollTop + track.clientHeight) {
        track.scrollTop = bottom - track.clientHeight;
      }
      return;
    }
    const left = active.offsetLeft;
    const right = left + active.offsetWidth;
    if (left < track.scrollLeft) track.scrollTo({ left, behavior: 'smooth' });
    else if (right > track.scrollLeft + track.clientWidth) {
      track.scrollTo({ left: right - track.clientWidth, behavior: 'smooth' });
    }
  }, [followedIndex, grid]);

  const navigate = (index: number, event: MouseEvent<HTMLButtonElement>) => {
    onNavigate(index, event.shiftKey);
  };

  const navigateWithKeyboard = (index: number, event: KeyboardEvent<HTMLButtonElement>) => {
    let nextIndex: number | null = null;
    if (event.key === 'ArrowLeft') nextIndex = Math.max(0, index - 1);
    else if (event.key === 'ArrowRight') nextIndex = Math.min(measures.length - 1, index + 1);
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = measures.length - 1;
    if (nextIndex == null || nextIndex === index) return;
    event.preventDefault();
    onNavigate(nextIndex, event.shiftKey);
    requestAnimationFrame(() => {
      trackRef.current
        ?.querySelector<HTMLButtonElement>(`[data-form-index="${nextIndex}"]`)
        ?.focus({ preventScroll: true });
    });
  };

  return (
    <nav
      className={grid ? 'score-form-overview score-form-overview-grid' : 'score-form-overview'}
      aria-label="Score form overview"
      data-testid="score-form-overview"
    >
      <span className="score-form-overview-label" aria-hidden="true">
        Form
      </span>
      <div ref={trackRef} className="score-form-overview-track">
        {measures.map((measure, index) => {
          const structure = structureLabel(measure);
          const selected = selectedIds.has(measure.id);
          const visible = visibleIds.has(measure.id);
          const playing = playingIndex === index;
          return (
            <button
              key={measure.id}
              type="button"
              className="score-form-overview-bar"
              data-form-index={index}
              data-active={activeIndex === index ? 'true' : undefined}
              data-selected={selected ? 'true' : undefined}
              data-visible={visible ? 'true' : undefined}
              data-playing={playing ? 'true' : undefined}
              aria-current={activeIndex === index ? 'location' : undefined}
              aria-pressed={selected}
              aria-keyshortcuts="ArrowLeft ArrowRight Home End Shift+ArrowLeft Shift+ArrowRight"
              aria-label={`Bar ${index + 1}${structure ? `, ${structure}` : ''}${visible ? ', visible in score' : ''}${playing ? ', playing' : ''}`}
              title={`Bar ${index + 1}${structure ? ` · ${structure}` : ''}\nClick to jump · Shift-click to select a passage`}
              onClick={(event) => navigate(index, event)}
              onKeyDown={(event) => navigateWithKeyboard(index, event)}
            >
              {measure.section && (
                <span className="score-form-overview-section" aria-hidden="true">
                  {measure.section}
                </span>
              )}
              <span className="score-form-overview-number" aria-hidden="true">
                {index + 1}
              </span>
              <span className="score-form-overview-marks" aria-hidden="true">
                {measure.open === 'repeat' && <span>𝄆</span>}
                {measure.ending != null && <span>{measure.ending || '⌜'}</span>}
                {measure.barRepeat != null && <span>{measure.barRepeat === 2 ? '%%' : '%'}</span>}
                {measure.segno && <span>𝄋</span>}
                {measure.coda && <span>⊕</span>}
                {measure.close === 'repeat' && <span>𝄇</span>}
                {measure.close === 'double' && <span>‖</span>}
                {measure.close === 'final' && <span>Ⅱ</span>}
              </span>
              {measure.timeSig && index > 0 && (
                <span className="score-form-overview-meter" aria-hidden="true">
                  {measure.timeSig[0]}/{measure.timeSig[1]}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
